import {
  Activity,
  AlertTriangle,
  Award,
  Calendar,
  ChevronLeft,
  CircleStop,
  Compass,
  Gauge,
  Mountain,
  Navigation,
  Power,
  Radio,
  Route,
  Smartphone,
  Timer,
  TrendingUp,
  Zap,
} from 'lucide-react';
import React from 'react';
import Card from '../ui/card';

const DEFAULT_DETAIL_IMAGE =
  'https://images.unsplash.com/photo-1605559424843-9e4c228bf1c2?w=200&h=140&fit=crop&q=80';

function IndianLicensePlate({ plate }) {
  const text = String(plate ?? '—').toUpperCase();
  return (
    <div
      className="inline-flex overflow-hidden rounded border-2 border-slate-900 bg-white text-sm font-bold tracking-wide text-slate-900"
      aria-label={`License plate ${text}`}
    >
      <span className="flex w-9 shrink-0 flex-col items-center justify-center bg-[#1e3a8a] px-0.5 py-1 text-[8px] font-semibold leading-tight text-white">
        <span>IND</span>
        <span className="mt-0.5 h-2 w-2 rounded-full bg-amber-400" aria-hidden />
      </span>
      <span className="flex items-center px-2.5 py-1.5">{text}</span>
    </div>
  );
}

function StatusBadge({ status }) {
  const working = String(status ?? 'WORKING').toUpperCase() === 'WORKING';
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
        working ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'
      }`}
    >
      <span
        className={`h-2 w-2 rounded-full ${working ? 'bg-emerald-500' : 'bg-slate-400'}`}
        aria-hidden
      />
      {String(status ?? 'WORKING').toUpperCase()}
    </span>
  );
}

function MetricCell({ icon: Icon, iconBg, iconColor, label, value, unit, className = '' }) {
  return (
    <div className={`flex min-w-0 flex-1 items-center gap-3 px-4 py-4 ${className}`}>
      <span
        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${iconBg}`}
        aria-hidden
      >
        <Icon className={`h-5 w-5 ${iconColor}`} strokeWidth={2} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-slate-500">{label}</p>
        <p className="mt-0.5 truncate text-xl font-bold text-slate-900">
          {value}
          {unit ? (
            <span className="ml-1 text-sm font-normal text-slate-500">{unit}</span>
          ) : null}
        </p>
      </div>
    </div>
  );
}

function MetricRow({ left, right }) {
  return (
    <div className="flex border-b border-slate-100">
      {left}
      <div className="w-px shrink-0 self-stretch bg-slate-100" aria-hidden />
      {right}
    </div>
  );
}

function SectionHeader({ title }) {
  return (
    <div className="border-b border-slate-100 bg-slate-50/80 px-4 py-2.5">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {title}
      </h2>
    </div>
  );
}

function SpeedStatsRow({ avg, max, p85 }) {
  return (
    <div className="grid grid-cols-3 border-b border-slate-100">
      {[
        { label: 'Avg speed', value: avg, unit: 'km/h' },
        { label: 'Max speed', value: max, unit: 'km/h' },
        { label: '85th speed', value: p85, unit: 'km/h' },
      ].map((stat, index) => (
        <div
          key={stat.label}
          className={`flex flex-col items-center px-2 py-4 text-center ${
            index > 0 ? 'border-l border-slate-100' : ''
          }`}
        >
          <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
            {stat.label}
          </p>
          <p className="mt-1 text-lg font-bold text-slate-900">
            {stat.value}
            <span className="ml-0.5 text-xs font-normal text-slate-500">{stat.unit}</span>
          </p>
        </div>
      ))}
    </div>
  );
}

function StopDwellRow({ name, arrivedAt, dwell }) {
  return (
    <div className="flex items-start gap-3 border-b border-slate-50 px-4 py-3 last:border-b-0">
      <span
        className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-50 text-amber-600"
        aria-hidden
      >
        <CircleStop className="h-4 w-4" strokeWidth={2} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-semibold text-slate-800">{name}</p>
        <p className="mt-0.5 text-xs text-slate-500">Arrived {arrivedAt}</p>
        <p className="mt-1 text-[11px] font-medium text-slate-600">Dwell {dwell}</p>
      </div>
    </div>
  );
}

function RoutePreview({ summary, distance, duration }) {
  return (
    <div className="border-b border-slate-100 px-4 py-4">
      <div className="overflow-hidden rounded-lg border border-slate-100 bg-gradient-to-br from-sky-50 to-slate-50">
        <div className="flex h-24 items-center justify-center">
          <Navigation className="h-10 w-10 text-sky-300" strokeWidth={1.5} aria-hidden />
        </div>
        <div className="border-t border-slate-100 bg-white px-3 py-2.5">
          <p className="truncate text-xs font-semibold text-slate-800">{summary}</p>
          <p className="mt-1 text-[11px] text-slate-500">
            {distance}
            <span className="mx-1.5 text-slate-300" aria-hidden>
              ·
            </span>
            {duration}
          </p>
        </div>
      </div>
    </div>
  );
}

function DriverScoreCell({ score, grade }) {
  const numeric = score != null && score !== '—' ? Number(score) : null;
  const scoreColor =
    numeric == null
      ? 'text-slate-400'
      : numeric >= 80
        ? 'text-emerald-600'
        : numeric >= 60
          ? 'text-amber-600'
          : 'text-rose-600';
  const ringColor =
    numeric == null
      ? 'border-slate-200'
      : numeric >= 80
        ? 'border-emerald-200'
        : numeric >= 60
          ? 'border-amber-200'
          : 'border-rose-200';

  return (
    <div className="flex items-center gap-4 border-b border-slate-100 px-4 py-4">
      <span
        className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-full border-4 bg-white ${ringColor}`}
        aria-hidden
      >
        <span className={`text-xl font-bold ${scoreColor}`}>{score}</span>
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-slate-500">Driver score</p>
        <p className="mt-0.5 text-xl font-bold text-slate-900">
          {grade}
          <span className="ml-1 text-sm font-normal text-slate-500">grade</span>
        </p>
        <p className="mt-1 text-xs text-slate-500">Based on driving behaviour today</p>
      </div>
      <Award className="h-8 w-8 shrink-0 text-violet-400" strokeWidth={1.5} aria-hidden />
    </div>
  );
}

function EmptyListMessage({ children }) {
  return (
    <p className="px-4 py-6 text-center text-xs text-slate-500">{children}</p>
  );
}

/** Vehicle telemetry detail view — shown inside VehicleDetails when a vehicle is selected. */
const VehicleInfoPanel = ({
  vehicle,
  listVehicle = null,
  isLoading = false,
  loadError = null,
  metricsError = null,
  onRetry,
  onBack,
}) => {
  const display = vehicle ?? listVehicle;
  if (!display && !isLoading) return null;

  const imageUrl =
    vehicle?.detail_image_url ??
    vehicle?.image_url ??
    listVehicle?.detail_image_url ??
    listVehicle?.image_url ??
    DEFAULT_DETAIL_IMAGE;

  const plate =
    vehicle?.license_plate ??
    vehicle?.licensePlate ??
    listVehicle?.license_plate ??
    '—';

  const recordedAt = vehicle?.recorded_at ?? listVehicle?.last_connected_at ?? '—';
  const lastConnected =
    vehicle?.last_connected_time ??
    vehicle?.last_connected_at ??
    listVehicle?.last_connected_at ??
    '—';

  const singleSpeed =
    vehicle?.speed_kmh != null ? Number(vehicle.speed_kmh).toFixed(1) : '—';

  const terminalId =
    vehicle?.terminal_phone ??
    listVehicle?.terminal_phone ??
    listVehicle?.patrol_id ??
    '—';

  const fmt = (v, digits = 1) =>
    v != null && !Number.isNaN(Number(v))
      ? Number(v).toLocaleString(undefined, { maximumFractionDigits: digits })
      : '—';

  const distanceKm = fmt(vehicle?.distance_km, 1);
  const tripCount = vehicle?.trip_count ?? '—';
  const idlingTime = vehicle?.idling_time ?? '—';
  const engineOnHours = fmt(vehicle?.engine_on_hours, 1);
  const harshAccel = vehicle?.harsh_acceleration_count ?? '—';
  const harshBraking = vehicle?.harsh_braking_count ?? '—';
  const harshCornering = vehicle?.harsh_cornering_count ?? '—';
  const overspeedEvents = vehicle?.overspeed_events_count ?? '—';
  const avgSpeed = fmt(vehicle?.avg_speed_kmh, 1);
  const maxSpeed = fmt(vehicle?.max_speed_kmh, 1);
  const speed85 = fmt(vehicle?.speed_85th_kmh, 1);
  const driverScore = vehicle?.driver_score ?? '—';
  const driverGrade = vehicle?.driver_grade ?? '—';

  const stops = Array.isArray(vehicle?.stops) ? vehicle.stops : [];
  const metricsLoaded = vehicle?.metrics_loaded === true;

  const routeSummary =
    vehicle?.route_summary ??
    (metricsLoaded ? 'No route data in the last 24 hours' : '—');
  const routeDistance =
    vehicle?.route_distance ?? (distanceKm !== '—' ? `${distanceKm} km` : '—');
  const routeDuration = vehicle?.route_duration ?? '—';

  return (
    <aside
      className="flex h-full min-h-0 w-full flex-col overflow-hidden rounded-xl border border-gray-200 bg-gradient-to-b from-slate-100 via-sky-50/80 to-sky-50/40 shadow-sm"
      aria-label="Vehicle information"
    >
      <Card.Light className="relative flex shrink-0 items-center justify-center rounded-b-none border-x-0 border-t-0 px-4 py-6 shadow-none">
        <button
          type="button"
          onClick={onBack}
          className="absolute left-4 flex h-10 w-10 items-center justify-center rounded-full bg-white text-black transition-colors hover:bg-slate-50"
          aria-label="Back to vehicle list"
        >
          <ChevronLeft className="h-5 w-5" strokeWidth={2} />
        </button>
        <h1 className="text-base font-bold text-black">Vehicle info</h1>
      </Card.Light>

      <div className="mx-3 my-3 flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-100 bg-white shadow-sm">
        {loadError ? (
          <div className="shrink-0 border-b border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
            <p>{loadError}</p>
            {onRetry ? (
              <button
                type="button"
                onClick={onRetry}
                className="mt-2 text-sm font-semibold text-red-800 underline"
              >
                Retry
              </button>
            ) : null}
          </div>
        ) : null}

        <div className="flex shrink-0 items-start gap-3 border-b border-slate-100 p-4">
          <img
            src={imageUrl}
            alt=""
            className="h-[88px] w-[120px] shrink-0 rounded-lg bg-slate-100 object-cover"
          />
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <StatusBadge status={vehicle?.status ?? (isLoading ? '…' : 'OFFLINE')} />
            <IndianLicensePlate plate={plate} />
            <div className="flex flex-col gap-1.5 text-xs text-slate-600">
              <div className="flex items-center gap-2">
                <Radio className="h-3.5 w-3.5 shrink-0 text-slate-400" strokeWidth={2} />
                <span>
                  <span className="text-slate-500">Last Connected At </span>
                  <span className="font-semibold text-slate-800">{lastConnected}</span>
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Smartphone className="h-3.5 w-3.5 shrink-0 text-slate-400" strokeWidth={2} />
                <span>
                  <span className="text-slate-500">Terminal ID </span>
                  <span className="font-semibold text-slate-800">
                    {isLoading && !vehicle ? '…' : terminalId}
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto custom-scrollbar">
          {isLoading && !vehicle ? (
            <div className="flex items-center justify-center px-4 py-16 text-sm text-slate-500">
              Loading vehicle details…
            </div>
          ) : (
            <>
              <MetricRow
                left={
                  <MetricCell
                    icon={Gauge}
                    iconBg="bg-violet-50"
                    iconColor="text-violet-600"
                    label="Recorder speed"
                    value={singleSpeed}
                    unit="km/h"
                  />
                }
                right={
                  <MetricCell
                    icon={Compass}
                    iconBg="bg-emerald-50"
                    iconColor="text-emerald-600"
                    label="Direction (Heading)"
                    value={vehicle?.direction_label ?? '—'}
                    unit={
                      vehicle?.direction_degrees != null
                        ? `${vehicle.direction_degrees}°`
                        : ''
                    }
                  />
                }
              />
              <MetricCell
                icon={Mountain}
                iconBg="bg-sky-50"
                iconColor="text-sky-600"
                label="Altitude (m)"
                value={vehicle?.altitude_m ?? '—'}
                unit="m"
                className="border-b border-slate-100"
              />
              <MetricCell
                icon={Calendar}
                iconBg="bg-amber-50"
                iconColor="text-amber-600"
                label="Recorded At"
                value={recordedAt}
                unit=""
              />

              {metricsError ? (
                <div className="border-b border-amber-100 bg-amber-50 px-4 py-2.5 text-xs text-amber-800">
                  Fleet KPIs unavailable: {metricsError}
                </div>
              ) : null}

              <SectionHeader title="Trip & distance" />
              <MetricCell
                icon={Route}
                iconBg="bg-sky-50"
                iconColor="text-sky-600"
                label="Distance"
                value={distanceKm}
                unit="km"
                className="border-b border-slate-100"
              />
              <MetricRow
                left={
                  <MetricCell
                    icon={Timer}
                    iconBg="bg-amber-50"
                    iconColor="text-amber-600"
                    label="Idling time"
                    value={idlingTime}
                    unit=""
                  />
                }
                right={
                  <MetricCell
                    icon={Power}
                    iconBg="bg-emerald-50"
                    iconColor="text-emerald-600"
                    label="Engine on hours"
                    value={engineOnHours}
                    unit="h"
                  />
                }
              />

              <SectionHeader title="Driving events" />
              <MetricRow
                left={
                  <MetricCell
                    icon={Zap}
                    iconBg="bg-amber-50"
                    iconColor="text-amber-600"
                    label="Harsh acceleration"
                    value={harshAccel}
                    unit="events"
                  />
                }
                right={
                  <MetricCell
                    icon={Activity}
                    iconBg="bg-rose-50"
                    iconColor="text-rose-600"
                    label="Harsh braking"
                    value={harshBraking}
                    unit="events"
                  />
                }
              />
              <MetricRow
                left={
                  <MetricCell
                    icon={TrendingUp}
                    iconBg="bg-violet-50"
                    iconColor="text-violet-600"
                    label="Harsh cornering"
                    value={harshCornering}
                    unit="events"
                  />
                }
                right={
                  <MetricCell
                    icon={AlertTriangle}
                    iconBg="bg-orange-50"
                    iconColor="text-orange-600"
                    label="Overspeed events"
                    value={overspeedEvents}
                    unit="events"
                  />
                }
              />

              <SectionHeader title="Speed statistics" />
              <SpeedStatsRow avg={avgSpeed} max={maxSpeed} p85={speed85} />

              <SectionHeader title="Route" />
              <RoutePreview
                summary={routeSummary}
                distance={routeDistance}
                duration={routeDuration}
              />

              <SectionHeader
                title={
                  tripCount !== '—'
                    ? `Stops / dwell · ${tripCount} trip${Number(tripCount) === 1 ? '' : 's'}`
                    : 'Stops / dwell'
                }
              />
              <div className="border-b border-slate-100">
                {stops.length > 0 ? (
                  stops.map((stop, index) => (
                    <StopDwellRow
                      key={`${stop.name}-${index}`}
                      name={stop.name}
                      arrivedAt={stop.arrivedAt}
                      dwell={stop.dwell}
                    />
                  ))
                ) : (
                  <EmptyListMessage>
                    {metricsLoaded
                      ? tripCount !== '—'
                        ? `No stops in the last 24h · ${tripCount} trip${Number(tripCount) === 1 ? '' : 's'} recorded.`
                        : 'No stops detected in the last 24 hours.'
                      : 'Stops and trip count load with fleet KPIs.'}
                  </EmptyListMessage>
                )}
              </div>

              <SectionHeader title="Driver score" />
              <DriverScoreCell score={driverScore} grade={driverGrade} />
            </>
          )}
        </div>
      </div>
    </aside>
  );
};

export default VehicleInfoPanel;
